import { useTranslation } from '@mint-lib/i18n';
import { Button } from '@mint-lib/tw-components';

import { useConnectionPlatform } from '../hooks/useConnectionPlatform.js';
import { PolicyMessage } from './PolicyMessage.js';

interface Props {
  disabled?: boolean;
  /** Datadog action name for the submit button, forwarded unchanged; pass a per-platform literal. */
  'data-dd-action-name': string;
  /** Set from `IntegrationMeta.requiresGooglePolicyNotice` — renders the Google API Services banner when true. */
  requiresGooglePolicyNotice?: boolean;
}

/**
 * The submit button (platform logo + dynamic "Connect {{platform}} Account"
 * copy) plus `PolicyMessage` every OAuth-flow add form ends with — identical
 * across Amazon and the generic omni platforms (mirrors legacy's
 * `AddConnectionOAuthForm`). Zemanta's basic-credentials flow uses
 * its own "Authorize" button instead, so this isn't shared with those.
 */
export function ConnectOAuthSubmit({
  disabled,
  requiresGooglePolicyNotice,
  'data-dd-action-name': actionName,
}: Props) {
  const { t } = useTranslation('@mint-lib/connections-ui');
  const platform = useConnectionPlatform();
  const logo = platform?.logo ?? platform?.iconUrl;

  return (
    <div className="flex flex-col justify-start gap-2">
      <div>
        <Button
          type="submit"
          variant="outlined"
          size="large"
          color="primary"
          disabled={disabled}
          data-dd-action-name={actionName}
        >
          {logo && (
            <img
              src={logo}
              alt={platform?.label || ''}
              className="mr-2.5 h-5 w-5"
            />
          )}
          {t('Connect {{platform}} Account', { platform: platform?.label })}
        </Button>
      </div>
      <PolicyMessage requiresGooglePolicyNotice={requiresGooglePolicyNotice} />
    </div>
  );
}
